import { useEffect, useMemo, useState } from 'react';
import { Modal } from './Modal';
import { useTemplates } from '../lib/templates';
import type { CreateSectionInput } from '../lib/sections';
import type { ProjectSection } from '@stromo/shared';

type Mode = 'empty' | 'duplicate' | 'from_template';

interface Props {
  open: boolean;
  sections: ProjectSection[];
  defaultName: string;
  saving?: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onSubmit: (input: CreateSectionInput) => void;
}

export function SectionCreateModal({
  open,
  sections,
  defaultName,
  saving,
  errorMessage,
  onCancel,
  onSubmit,
}: Props) {
  const [name, setName] = useState(defaultName);
  const [mode, setMode] = useState<Mode>('empty');
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState<number | null>(null);

  const templatesQ = useTemplates();
  const activeTemplates = useMemo(
    () => (templatesQ.data ?? []).filter((t) => t.is_active),
    [templatesQ.data],
  );

  // Beim Öffnen Default-Werte setzen. Beim Schliessen läuft nichts zurück,
  // das Modal wird via `open=false` aus dem DOM genommen und re-mounted
  // beim nächsten Öffnen — die useState-Defaults greifen wieder.
  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setMode('empty');
    setSourceId(sections[0]?.id ?? null);
    setTemplateId(null);
  }, [open, defaultName, sections]);

  // Erstes aktives Template als Default vorauswählen, sobald geladen
  useEffect(() => {
    if (templateId == null && activeTemplates.length > 0) {
      setTemplateId(activeTemplates[0]!.id);
    }
  }, [activeTemplates, templateId]);

  const canSubmit =
    name.trim().length > 0 &&
    (mode === 'empty' ||
      (mode === 'duplicate' && sourceId != null) ||
      (mode === 'from_template' && templateId != null));

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (mode === 'empty') {
      onSubmit({ mode: 'empty', name: trimmed });
    } else if (mode === 'duplicate' && sourceId != null) {
      onSubmit({ mode: 'duplicate', name: trimmed, source_section_id: sourceId });
    } else if (mode === 'from_template' && templateId != null) {
      onSubmit({ mode: 'from_template', name: trimmed, template_id: templateId });
    }
  };

  return (
    <Modal
      open={open}
      title="Neue Sektion"
      onClose={saving ? () => undefined : onCancel}
      width={520}
      footer={
        <>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={saving}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
          >
            {saving ? 'Lege an …' : 'Anlegen'}
          </button>
        </>
      }
    >
      {errorMessage && <div className="login-error">{errorMessage}</div>}

      <div className="form-group" style={{ marginBottom: 18 }}>
        <label className="form-label">Name *</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z.B. Haus 2"
          autoFocus
        />
      </div>

      <div className="form-label" style={{ marginBottom: 10 }}>Inhalt</div>
      <div className="section-mode-list">
        <ModeOption
          label="Leere Sektion"
          description="Anlegen ohne Aufgaben. Tasks können danach manuell ergänzt werden."
          checked={mode === 'empty'}
          onSelect={() => setMode('empty')}
        />

        <ModeOption
          label="Bestehende Sektion duplizieren"
          description="Übernimmt Aufgaben-Liste (Titel, Nummerierung, Gruppen) der gewählten Sektion. Termine, Status und Notizen bleiben leer."
          checked={mode === 'duplicate'}
          onSelect={() => setMode('duplicate')}
          disabled={sections.length === 0}
        >
          {mode === 'duplicate' && (
            <select
              value={sourceId ?? ''}
              onChange={(e) => setSourceId(e.target.value ? Number(e.target.value) : null)}
              style={{ marginTop: 10, width: '100%' }}
            >
              {sections.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
        </ModeOption>

        <ModeOption
          label="Aus Vorlage importieren"
          description="Legt für jede Position der gewählten Vorlage eine leere Aufgabe an — identisch zum Projekt-Anlegen."
          checked={mode === 'from_template'}
          onSelect={() => setMode('from_template')}
          disabled={activeTemplates.length === 0}
        >
          {mode === 'from_template' && (
            <>
              {templatesQ.isLoading ? (
                <div className="loading" style={{ marginTop: 10 }}>Lade Vorlagen …</div>
              ) : activeTemplates.length === 0 ? (
                <div style={{ marginTop: 10, fontSize: 13, color: 'var(--color-text-soft)' }}>
                  Keine aktiven Vorlagen verfügbar.
                </div>
              ) : (
                <select
                  value={templateId ?? ''}
                  onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
                  style={{ marginTop: 10, width: '100%' }}
                >
                  {activeTemplates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} (v{t.version})</option>
                  ))}
                </select>
              )}
            </>
          )}
        </ModeOption>
      </div>
    </Modal>
  );
}

function ModeOption(props: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  children?: React.ReactNode;
}) {
  const { label, description, checked, disabled, onSelect, children } = props;
  return (
    <label
      className={`section-mode-option${checked ? ' is-active' : ''}${disabled ? ' is-disabled' : ''}`}
      style={{
        display: 'block',
        padding: 14,
        border: checked ? '2px solid var(--color-primary)' : '1px solid var(--color-border-soft)',
        borderRadius: 8,
        marginBottom: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        background: checked ? 'rgba(46, 105, 173, 0.04)' : '#fff',
        transition: 'all .12s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <input
          type="radio"
          name="section-mode"
          checked={checked}
          onChange={() => { if (!disabled) onSelect(); }}
          disabled={disabled}
          style={{ marginTop: 2 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-navy)' }}>{label}</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 4, lineHeight: 1.5 }}>
            {description}
          </div>
          {children}
        </div>
      </div>
    </label>
  );
}
